import * as amplitude from "@amplitude/analytics-browser"
import type { SessionReplayOptions } from "@amplitude/plugin-session-replay-browser"
import { keepSearchParamsFromString } from "@tastyworks/shared-browser-utils/url"
import { UTM_PARAM_KEYS } from "@tastyworks/shared-browser-utils/utm"

export interface AmplitudeInitOptions {
  serverZone?: "US" | "EU"
  instanceName?: string
  optOut?: boolean
  identityStorage?: amplitude.Types.IdentityStorageType
}

export type SessionReplayConfig = Partial<SessionReplayOptions>

// Fallback only — Amplitude remote config overrides these.
const SESSION_REPLAY_DEFAULT_CONFIG: SessionReplayConfig = {
  sampleRate: 0,
  privacyConfig: { defaultMaskLevel: "conservative" },
  useWebWorker: true,
}

// UTM keys plus every click ID Amplitude's campaign parser tracks by default.
// Keys not listed here are stripped from URL event/user properties.
const AMPLITUDE_KEEP_PARAM_KEYS = [
  ...UTM_PARAM_KEYS,
  "utm_id",
  "dclid",
  "fbclid",
  "gbraid",
  "gclid",
  "ko_click_id",
  "li_fat_id",
  "msclkid",
  "rdt_cid",
  "ttclid",
  "twclid",
  "wbraid",
] as const

const AUTOCAPTURE_CONFIG: amplitude.Types.AutocaptureOptions = {
  pageViews: { trackHistoryChanges: "all" },
  sessions: true,
  attribution: true,
  formInteractions: true,
  webVitals: true,
  fileDownloads: false,
  // Volume heavy and PII sensitive interaction disabled by default.
  // They can be enabled in Amplitude Autocapture remote config
  elementInteractions: false,
  frustrationInteractions: false,
  networkTracking: false,
}
const HTTP_URL_RE = /^https?:\/\//i

let instance: amplitude.Types.BrowserClient | undefined

// An empty apiKey is how an environment opts out.
export function initAmplitude(
  apiKey: string,
  options?: AmplitudeInitOptions
): void {
  if (typeof window === "undefined" || instance || !apiKey) {
    return
  }

  instance = amplitude.createInstance()
  instance
    .init(apiKey, {
      autocapture: AUTOCAPTURE_CONFIG,
      fetchRemoteConfig: true,
      flushMaxRetries: 2,
      flushIntervalMillis: 5000,
      flushQueueSize: 20,
      ...options,
    })
    .promise.then(() => {
      instance!.add(makeStripQueryPlugin())
    })
    .catch((error) => {
      console.error("initAmplitude failed", error)
    })
}

export function setAmplitudeUserId(externalId: string | null): void {
  instance?.setUserId(externalId ?? undefined)
}

export function isAmplitudeInitialized(): boolean {
  return instance !== undefined
}

export function setAmplitudeOptOut(optOut: boolean): void {
  instance?.setOptOut(optOut)
}

let sessionReplayRegistered = false

export async function setSessionReplayEnabled(
  enable: boolean,
  config: SessionReplayConfig = {}
): Promise<void> {
  if (!instance || !enable || sessionReplayRegistered) {
    return
  }
  sessionReplayRegistered = true
  try {
    const { sessionReplayPlugin } =
      await import("@amplitude/plugin-session-replay-browser")
    instance.add(
      sessionReplayPlugin({ ...SESSION_REPLAY_DEFAULT_CONFIG, ...config })
    )
  } catch (error) {
    sessionReplayRegistered = false
    console.error("setSessionReplayEnabled failed", error)
  }
}

// Several routes carry tokens, secrets, and PII in query strings (e.g. password
// reset tokens, email confirmation tokens, joint-signup PII). Strip everything
// except UTM keys and vendor click IDs (needed by Amplitude's attribution
// parser) from URL properties before events leave the browser.
function stripUrlsFlat(obj: Record<string, any>): void {
  for (const key of Object.keys(obj)) {
    const value = obj[key]
    if (typeof value === "string" && HTTP_URL_RE.test(value)) {
      obj[key] = keepSearchParamsFromString(value, AMPLITUDE_KEEP_PARAM_KEYS)
    }
  }
}

function makeStripQueryPlugin(): amplitude.Types.EnrichmentPlugin {
  return {
    name: "strip-sensitive-query-params",
    execute: async (event: amplitude.Types.Event) => {
      if (event.event_properties) {
        stripUrlsFlat(event.event_properties)
      }
      const userProps = event.user_properties as Record<string, any> | undefined
      if (userProps) {
        stripUrlsFlat(userProps)
        if (userProps.$set && typeof userProps.$set === "object") {
          stripUrlsFlat(userProps.$set)
        }
        if (userProps.$setOnce && typeof userProps.$setOnce === "object") {
          stripUrlsFlat(userProps.$setOnce)
        }
      }
      return event
    },
  }
}
